/**
 * Admin Action Buttons API client — /api/admin-action-buttons.php.
 * Credentialed (admin session). Backs the super-admin "Action Buttons" designer
 * for the estimate-results "Next Steps" bar.
 */

import type { ActionButtonConfig } from '../config/actionButtons';

const API_BASE = '/api';

export interface AdminActionBar {
  id: number;
  /** null = the universal default bar. */
  retailerId: number | null;
  buttons: ActionButtonConfig[];
  isActive: boolean;
}

export interface ActionButtonRetailerSummary {
  id: number;
  name: string;
  isPartner: boolean;
}

export interface ActionButtonsListResponse {
  bars: AdminActionBar[];
  retailers: ActionButtonRetailerSummary[];
  /** Built-in default set — for "Reset" and pre-filling a new retailer override. */
  defaults: ActionButtonConfig[];
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export async function fetchActionBars(): Promise<ActionButtonsListResponse> {
  const res = await fetch(`${API_BASE}/admin-action-buttons.php`, { credentials: 'include' });
  return handle<ActionButtonsListResponse>(res);
}

/** Upsert one bar. `retailerId` null = the universal default. */
export async function saveActionBar(
  retailerId: number | null,
  buttons: ActionButtonConfig[],
): Promise<{ bar: AdminActionBar }> {
  const res = await fetch(`${API_BASE}/admin-action-buttons.php`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ retailerId, buttons }),
  });
  return handle(res);
}

/**
 * Delete a retailer override (revert to universal). Passing null targets the
 * universal row, which the server resets to the built-in defaults rather than
 * removing.
 */
export async function deleteActionBar(retailerId: number | null): Promise<void> {
  const q = retailerId === null ? 'universal' : String(retailerId);
  const res = await fetch(`${API_BASE}/admin-action-buttons.php?retailer_id=${encodeURIComponent(q)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Delete failed (${res.status})`);
  }
}
